import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import sperologo from "../../../../assets/sperologo.png";
import { API_URL } from "../../../Config/api";

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  page: {
    paddingTop: 0,
    paddingHorizontal: 24,
    // ✅ footer ke liye reserved space
    fontSize: 10,
    fontFamily: "Helvetica",
    lineHeight: 1.4,
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #ccc",
    paddingBottom: 8,
    marginBottom: 12,
  },

  headerTitle: {
    fontSize: 14,
    fontWeight: "bold",
    border: "2px solid #000",
    padding: "4 8",
  },

  logo: { width: 70 },

  section: {
    border: "1px solid #cfcfcf",
    padding: 10,
    marginBottom: 12,
    borderRadius: 6,
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 6,
    borderLeft: "5px solid #16c7c7",
    paddingLeft: 6,
  },

  field: { marginBottom: 6 },

  label: {
    fontSize: 10,
    fontWeight: "bold",
  },

  value: {
    fontSize: 10,
    color: "#555",
  },

  twoColRow: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 6,
  },

  halfCol: {
    width: "50%",
  },

  fullCol: {
    width: "100%",
  },

  threeColRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },

  field3: {
    width: "33.33%",
    paddingRight: 6,
    marginBottom: 6,
  },
  divider: {
    borderBottom: "1px solid #d0d0d0",
    marginVertical: 6,
  },

  signature: {
    height: 80,
    border: "1px dashed #777",
    marginTop: 6,
  },
  subLabel: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 2,
  },

  subHeading: {
    fontSize: 11,
    fontWeight: "bold",
    marginTop: 6,
    marginBottom: 4,
  },

  grid3: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },

  cell: {
    width: "32%",
  },

  line: {
    borderBottom: "1px solid #ccc",
    marginVertical: 6,
  },

  footer: {
    position: "fixed",
    bottom: -10, // ✅ exact page end
    left: 24,
    right: 24,
    height: 40,
    borderTop: "1px solid #ccc",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 6,
  },

  footerCenter: {
    flex: 1,
    alignItems: "center",
  },

  footerRight: {
    position: "absolute",
    right: 0,
    fontSize: 9,
    color: "#555",
  },

  footerLogo: {
    width: 60,
    height: 22,
    objectFit: "contain",
  },
  bodyTable: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: "#ccc",
  },

  bodyHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f2f2f2",
    borderBottomWidth: 1,
    borderColor: "#ccc",
    paddingVertical: 4,
  },

  bodyRow: {
    flexDirection: "row",
    alignItems: "flex-start", // 🔥 important
    borderBottomWidth: 1,
    borderColor: "#e0e0e0",
    paddingVertical: 6,
  },

  bodyCellPart: {
    width: "30%",
    fontSize: 10,
    fontWeight: "bold",
    paddingHorizontal: 4,
  },

  bodyCellYes: {
    width: "15%",
    fontSize: 10,
    textAlign: "center",
    paddingTop: 2,
  },

  bodyCellRemark: {
    width: "55%",
    fontSize: 10,
    paddingHorizontal: 4,
    lineHeight: 1.4, // 🔥 multiline readability
    flexWrap: "wrap", // 🔥 MUST
  },
  assessmentImage: {
    width: "100%",
    maxHeight: 300, // 🔥 prevents layout break
    objectFit: "contain",
    marginTop: 6,
  },
  rowTwoCol: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },

  leftBox: {
    width: "65%",
    paddingRight: 6,
  },

  rightBox: {
    width: "35%",
    alignItems: "center",
  },
});

/* ================= HELPERS ================= */

const Field = ({ label, value }) => (
  <View style={styles.field}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || "—"}</Text>
  </View>
);

const Section = ({ title, children }) => (
  <View style={styles.section} wrap={false}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {children}
  </View>
);
const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const formatDateOnly = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const BODY_PART_CONFIG = [
  { key: "heent", label: "HEENT" },
  { key: "neck", label: "Neck" },
  { key: "chest", label: "Chest" },
  { key: "abdomen", label: "Abdomen" },
  { key: "pelvis", label: "Pelvis" },
  { key: "back", label: "Back" },
  { key: "upper_limb", label: "Upper Limb" },
  { key: "lower_limb", label: "Lower Limb" },
  { key: "general", label: "General" },
];

const buildBodyAssessmentRows = (data) =>
  BODY_PART_CONFIG.map(({ key, label }) => ({
    part: label,
    yes_no: data?.[key] === true ? "Yes" : data?.[key] === false ? "No" : "—",
    remark: data?.[`${key}_remark`] || "—",
  }));

  const getFormattedAge = (data) => {
  const parts = [];

  if (data?.Age !== null && data?.Age !== undefined)
    parts.push(`${data.Age} Y`);

  if (data?.Age_month !== null && data?.Age_month !== undefined)
    parts.push(`${data.Age_month} M`);

  if (data?.Age_day !== null && data?.Age_day !== undefined)
    parts.push(`${data.Age_day} D`);

  return parts.length ? parts.join(" ") : "-";
};

/* ================= PDF ================= */

const LongTermReportPDF = ({ reportData }) => {
  const bodyAssessmentRows = buildBodyAssessmentRows(reportData);
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View
          fixed
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: 72,
            flexDirection: "row",
            alignItems: "center",
            paddingHorizontal: 24,
            backgroundColor: "#ffffff",
            borderBottom: "2px solid #16c7c7",
          }}
        >
          {/* LEFT : FORM TITLE */}
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontSize: 18,
                fontWeight: "bold",
                color: "#16c7c7",
                letterSpacing: 1.4,
              }}
            >
              LONG TERM FORM
            </Text>
          </View>

          {/* RIGHT : LOGO + HHC CODE */}
          <View
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Image
              src={sperologo}
              style={{
                width: 78,
                height: 36,
                objectFit: "contain",
              }}
            />

            <Text
              style={{
                fontSize: 9,
                marginTop: 4,
                color: "#333",
                fontWeight: "bold",
                letterSpacing: 0.6,
              }}
            >
              HHC No: {reportData?.hhc_code || "—"}
            </Text>
          </View>
        </View>

        <View
          style={{
            position: "fixed",
            top: 60,
            left: 24,
            right: 24,
            height: 1,
            backgroundColor: "#e0e0e0",
          }}
          fixed
        />
        <View style={{ height: 25 }} fixed />

        {/* 1️⃣ PERSONAL INFORMATION */}
        <Section title="Personal Information" break>
          <View style={styles.divider} />
          <View style={styles.threeColRow}>
            {[
              ["Name", reportData?.name],
              ["Patient Number", reportData?.phone_no],
              ["Date of Birth", reportData?.dob],
              ["Age", getFormattedAge(reportData)],
              ["Gender", reportData?.gender_id],
              ["Marital Status", reportData?.marital_status],
              ["Blood Group", reportData?.blood_group],
              ["Height", reportData?.height],
              ["Weight", reportData?.weight],
              ["Identification Mark", reportData?.identification_mark],
              ["State", reportData?.state_id],
              ["City", reportData?.city_id],
              ["Address", reportData?.address],
              ["Pincode", reportData?.pincode],
              ["Allergies", reportData?.allergies],
              ["Vaccinations", reportData?.vaccinations],
              ["Insurance Provider", reportData?.insurance],
              ["Family Doctor Name", reportData?.family_doctor],
              ["Family Doctor Phone", reportData?.family_doctor_phone],
              ["Family Doctor Email", reportData?.family_doctor_email],
              ["Spouse Name", reportData?.spouse_name],
              ["Spouse Phone", reportData?.spouse_phone],
              ["Spouse Email", reportData?.spouse_email],
              ["Past Medical History", reportData?.past_medical_history],
              ["Tobacco Remark", reportData?.tobacco_remark],
              ["Alcohol Remark", reportData?.alcohol_remark],
            ].map(([label, value], i) => (
              <View key={i} style={styles.field3}>
                <Field label={label} value={value} />
              </View>
            ))}
          </View>
        </Section>

        {/* 2️⃣ DIAGNOSIS */}
        <Section title="Diagnosis / Medical" break>
          <View style={styles.divider} />
          <View style={styles.threeColRow}>
            {[
              ["Diagnosis", reportData?.diagnosis],
              ["Hospital ", reportData?.hosp_id],
              ["Primary Consultant", reportData?.primary_consultant_name],
              [
                "Primary Consultant Phone",
                reportData?.primary_consultant_phone,
              ],
              [
                "Primary Consultant Email",
                reportData?.primary_consultant_email,
              ],
              ["Other Consultant", reportData?.other_consultant_name],
              ["Other Consultant Phone", reportData?.other_consultant_phone],
              ["Other Consultant Email", reportData?.other_consultant_email],
              [
                "Duration From - To",
                `${formatDateOnly(
                  reportData?.hospitalization_from
                )} to ${formatDateOnly(reportData?.hospitalization_to)}`,
              ],
            ].map(([label, value], i) => (
              <View key={i} style={styles.field3}>
                <Field label={label} value={value} />
              </View>
            ))}
          </View>
        </Section>

        {/* 3️⃣ CURRENT TREATMENT PLAN */}
        <Section title="Current Treatment Plan" break>
          <View style={styles.divider} />

          {/* Row 1 */}
          <View style={styles.twoColRow}>
            <View style={styles.halfCol}>
              <Field label="Tablet" value={reportData?.ctp_tablet} />
            </View>
            <View style={styles.halfCol}>
              <Field label="Injection" value={reportData?.ctp_injection} />
            </View>
          </View>

          {/* Row 2 */}
          <View style={styles.twoColRow}>
            <View style={styles.halfCol}>
              <Field label="IV Fluids" value={reportData?.ctp_iv_fluids} />
            </View>
            <View style={styles.halfCol}>
              <Field
                label="Interventions"
                value={reportData?.ctp_interventions}
              />
            </View>
          </View>

          {/* Notes Full Width */}
          <View style={styles.fullCol}>
            <Field label="Notes" value={reportData?.ctp_notes} />
          </View>
        </Section>

        {/* 4️⃣ FOLLOW UP INSTRUCTIONS */}
        <Section title="Follow-up Instructions" break>
          <View style={styles.divider} />

          {/* Row 1 */}
          <View style={styles.twoColRow}>
            <View style={styles.halfCol}>
              <Field label="Physical" value={reportData?.fi_physical} />
            </View>
            <View style={styles.halfCol}>
              <Field
                label="Investigations"
                value={reportData?.fi_investigations}
              />
            </View>
          </View>

          {/* Row 2 */}
          <View style={styles.twoColRow}>
            <View style={styles.halfCol}>
              <Field
                label="Interventions"
                value={reportData?.fi_interventions}
              />
            </View>
            <View style={styles.halfCol}>
              <Field label="Assistance" value={reportData?.fi_assistance} />
            </View>
          </View>

          {/* Referred By */}
          <View style={styles.threeColRow}>
            <View style={styles.field3}>
              <Field label="Referred By" value={reportData?.reffered_by} />
            </View>
            <View style={styles.field3}>
              <Field
                label="Referred Phone"
                value={reportData?.reffered_by_phone}
              />
            </View>
            <View style={styles.field3}>
              <Field
                label="Referred Email"
                value={reportData?.reffered_by_email}
              />
            </View>
          </View>
        </Section>
        <Section title="Assessment by Physician" break>
          {/* Top row */}
          <View style={{ flexDirection: "row", gap: 12, marginBottom: 8 }}>
            <View style={{ width: "50%" }}>
              <Text style={styles.subLabel}>Assessment place</Text>
              <Text>{reportData?.assessment_place || "—"}</Text>
            </View>

            <View style={{ width: "50%" }}>
              <Text style={styles.subLabel}>Physician Name</Text>
              <Text>{reportData?.doctor_name || "—"}</Text>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.line} />

          {/* BASELINE heading */}
          <Text style={styles.subHeading}>Baseline</Text>

          {/* First vitals row */}
          <View style={styles.grid3}>
            <View style={styles.cell}>
              <Text style={styles.subLabel}>AVPU</Text>
              <Text>{reportData?.baseline || "—"}</Text>
            </View>

            <View style={styles.cell}>
              <Text style={styles.subLabel}>Airway</Text>
              <Text>{reportData?.airway || "—"}</Text>
            </View>

            <View style={styles.cell}>
              <Text style={styles.subLabel}>Breathing</Text>
              <Text>{reportData?.breathing || "—"}</Text>
            </View>
          </View>

          {/* Second row */}
          <View style={styles.grid3}>
            <View style={styles.cell}>
              <Text style={styles.subLabel}>Circulation</Text>
              <Text>{reportData?.circulation || "—"}</Text>
            </View>

            <View style={styles.cell}>
              <Text style={styles.subLabel}>Skin Perfusion</Text>
              <Text>{reportData?.skin_perfusion || "—"}</Text>
            </View>

            <View style={styles.cell}>
              <Text style={styles.subLabel}>Temp (°F)</Text>
              <Text>{reportData?.temp || "—"}</Text>
            </View>
          </View>

          {/* Third vitals row */}
          <View style={styles.grid3}>
            <View style={styles.cell}>
              <Text style={styles.subLabel}>Pulse (/min)</Text>
              <Text>{reportData?.pulse || "—"}</Text>
            </View>

            <View style={styles.cell}>
              <Text style={styles.subLabel}>RR (/min)</Text>
              <Text>{reportData?.rr || "—"}</Text>
            </View>

            <View style={styles.cell}>
              <Text style={styles.subLabel}>BP (mmHg)</Text>
              <Text>{reportData?.bp || "—"}</Text>
            </View>
          </View>

          {/* Fourth vitals row */}
          <View style={styles.grid3}>
            <View style={styles.cell}>
              <Text style={styles.subLabel}>BSL (mg/dl)</Text>
              <Text>{reportData?.bsl || "—"}</Text>
            </View>

            <View style={styles.cell}>
              <Text style={styles.subLabel}>SpO₂ (%)</Text>
              <Text>{reportData?.spo2 || "—"}</Text>
            </View>

            <View style={styles.cell}>
              <Text style={styles.subLabel}>GCS (Out of 15)</Text>
              <Text>{reportData?.gst_total || "—"}</Text>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.line} />

          {/* Clinical impression */}
          <Text style={styles.subHeading}>Clinical Impression</Text>
          <Text>{reportData?.clinical_impression || "—"}</Text>
        </Section>
       <Section title="Recent Investigations" break>
  <View style={styles.divider} />

  {/* Row 1 */}
  <View style={styles.twoColRow}>
    <View style={styles.halfCol}>
      <Field label="CBC" value={reportData?.recent_invesigation_cbc} />
    </View>
    <View style={styles.halfCol}>
      <Field label="RFT" value={reportData?.rft} />
    </View>
  </View>

  {/* Row 2 */}
  <View style={styles.twoColRow}>
    <View style={styles.halfCol}>
      <Field label="Electrolytes" value={reportData?.electrocycles} />
    </View>
    <View style={styles.halfCol}>
      <Field label="Other" value={reportData?.other} />
    </View>
  </View>

  {/* Row 3 – Full Width */}
  {/* <View style={styles.fullRow}>
    <Field label="Other" value={reportData?.other} />
  </View> */}
</Section>


        <Section title="Assessment Body" break>
          <View style={styles.divider} />

          <View style={styles.rowTwoCol}>
            {/* LEFT */}
            <View style={styles.leftBox}>
              <Text style={styles.subHeading}>Assessment Body Remark</Text>
              <Text>{reportData?.assessment_body_remark || "—"}</Text>

              <View style={styles.line} />

              <Text style={styles.subHeading}>General Body Notes</Text>
              <Text>{reportData?.General_Body_Notes || "—"}</Text>

              <View style={styles.line} />

              <Text style={styles.subHeading}>Body Part Yes / No / Remark</Text>

              {bodyAssessmentRows.map((item, i) => (
                <View key={i} style={styles.bodyRow}>
                  <Text style={styles.bodyCellPart}>{item.part}</Text>
                  <Text style={styles.bodyCellYes}>{item.yes_no}</Text>
                  <Text style={styles.bodyCellRemark}>{item.remark}</Text>
                </View>
              ))}
            </View>

            {/* RIGHT */}
            <View style={styles.rightBox} wrap={false}>
              <Text style={styles.subHeading}>Assessment Body Image</Text>

              {reportData?.patient_image ? (
                <Image
                  src={`${API_URL}${reportData.patient_image}`}
                  style={styles.assessmentImage}
                />
              ) : (
                <Text>Image not available</Text>
              )}
            </View>
          </View>
        </Section>

        {/* 5️⃣ HHC SERVICES + SUB-SERVICES */}
        <Section title="HHC Service Reference" break>
          {/* Divider */}
          <View
            style={{ height: 1, backgroundColor: "#ccc", marginVertical: 4 }}
          />

          {/* ===== Select Services ===== */}
          <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 4 }}>
            Select Services
          </Text>

          {(reportData?.services || []).map((item, i) => (
            <View key={i} style={{ marginBottom: 6 }}>
              {/* Main Service */}
              <Text style={{ fontSize: 11, fontWeight: 600 }}>
                • {item?.service_name}
              </Text>

              {/* Sub Services */}
              {(item?.sub_services || []).map((sub, j) => (
                <Text
                  key={j}
                  style={{
                    marginLeft: 14,
                    fontSize: 10,
                  }}
                >
                  – {sub}
                </Text>
              ))}
            </View>
          ))}

          {/* ===== Patient / Guardian Details ===== */}
          <Text
            style={{
              fontSize: 11,
              fontWeight: "bold",
              marginTop: 6,
              marginBottom: 3,
            }}
          >
            Patient / Guardian Details
          </Text>

          {/* Header Row */}
          <View
            style={{
              flexDirection: "row",
              borderBottomWidth: 1,
              borderColor: "#ccc",
              paddingBottom: 2,
            }}
          >
            <Text style={{ width: "33%", fontSize: 10, fontWeight: "bold" }}>
              Patient Name
            </Text>
            <Text style={{ width: "33%", fontSize: 10, fontWeight: "bold" }}>
              Form Fill-up Date & Time
            </Text>
            <Text style={{ width: "34%", fontSize: 10, fontWeight: "bold" }}>
              Physician Name
            </Text>
          </View>

          {/* Value Row */}
          <View style={{ flexDirection: "row", paddingTop: 2 }}>
            <Text style={{ width: "33%", fontSize: 10 }}>
              {reportData?.name || "-"}
            </Text>

            <Text style={{ width: "33%", fontSize: 10 }}>
              {formatDateTime(reportData?.form_fillup_date)}
            </Text>

            <Text style={{ width: "34%", fontSize: 10 }}>
              {reportData?.notify_name || "-"}
            </Text>
          </View>

          {/* ===== Signatures ===== */}
          <View style={{ flexDirection: "row", marginTop: 10 }}>
            {/* Patient Signature */}
            <View style={{ width: "50%", paddingRight: 6 }}>
              <Text style={{ fontSize: 11, marginBottom: 4 }}>
                Patient / Guardian Signature
              </Text>

              {reportData?.patient_signature ? (
                <Image
                  src={`${API_URL}${reportData.patient_signature}`}
                  style={{
                    width: 120,
                    height: 60,
                    borderWidth: 1,
                    borderStyle: "dashed",
                  }}
                />
              ) : (
                <View
                  style={{
                    width: 120,
                    height: 60,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ fontSize: 9 }}>Patient Signature</Text>
                </View>
              )}
            </View>

            {/* Physician Signature */}
            <View
              style={{
                width: "50%",
                paddingLeft: 6,
                alignItems: "flex-end", // 🔥 right shift
              }}
            >
              <Text
                style={{
                  fontSize: 11,
                  marginBottom: 4,
                  alignSelf: "flex-end", // text bhi right align
                }}
              >
                Physician Signature
              </Text>

              {reportData?.physician_signature ? (
                <Image
                  src={`${API_URL}${reportData.physician_signature}`}
                  style={{
                    width: 120,
                    height: 60,
                    borderWidth: 1,
                    borderStyle: "dashed",
                  }}
                />
              ) : (
                <View
                  style={{
                    width: 120,
                    height: 60,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ fontSize: 9 }}>Physician Signature</Text>
                </View>
              )}
            </View>
          </View>
        </Section>

        {/* FOOTER */}
      </Page>
    </Document>
  );
};

export default LongTermReportPDF;
